import type { Id, TransferLine } from "@estelle/domain";

/**
 * One line of a transfer still being drafted, before it is sent.
 *
 * `quantity` stays a string for the lifetime of the draft — it is what a
 * controlled `<input type="number">` needs to hold "" or "3." while someone
 * is mid-keystroke, neither of which `Number` should be asked to represent.
 * `key` is a stable react key independent of `variantId`, because two lines
 * of the same variant is a real (if unusual) request and `variantId` alone
 * would collide as a key before it collides as a business rule.
 */
export interface CreateLineDraft {
  key: string;
  variantId: string;
  quantity: string;
}

export function emptyLine(key: string): CreateLineDraft {
  return { key, variantId: "", quantity: "1" };
}

/** A positive integer, the only quantity a transfer line accepts. */
function parsePositiveInt(value: string): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return parsed > 0 ? parsed : null;
}

/**
 * Whether the draft has enough to submit.
 *
 * Deliberately does not check `fromWarehouseId !== toWarehouseId` — the
 * fixture adapter refuses that with an `invalid` rejection, and that
 * refusal has to reach the screen and render, not be swallowed by a
 * disabled button pretending the rule lives here too (M6-14).
 */
export function isCreateDraftComplete(draft: {
  fromWarehouseId: string;
  toWarehouseId: string;
  lines: CreateLineDraft[];
}): boolean {
  if (draft.fromWarehouseId === "" || draft.toWarehouseId === "") {
    return false;
  }
  if (draft.lines.length === 0) return false;
  return draft.lines.every(
    (line) => line.variantId !== "" && parsePositiveInt(line.quantity) !== null,
  );
}

export function buildCreateTransferInput(draft: {
  fromWarehouseId: Id;
  toWarehouseId: Id;
  note: string;
  lines: CreateLineDraft[];
}): {
  fromWarehouseId: Id;
  toWarehouseId: Id;
  note?: string;
  lines: { variantId: Id; quantity: number }[];
} {
  return {
    fromWarehouseId: draft.fromWarehouseId,
    toWarehouseId: draft.toWarehouseId,
    ...(draft.note.trim() === "" ? {} : { note: draft.note.trim() }),
    lines: draft.lines.map((line) => ({
      variantId: line.variantId,
      // Safe: only called once `isCreateDraftComplete` has passed.
      quantity: parsePositiveInt(line.quantity) ?? 0,
    })),
  };
}

/**
 * What is still owed on one line — `quantityDispatched - quantityReceived`.
 *
 * The ceiling a receipt input is allowed to reach. Never negative: a fully
 * received line has nothing left to enter, not a line the input would let
 * someone push below zero.
 */
export function remainingToReceive(line: TransferLine): number {
  return Math.max(0, line.quantityDispatched - line.quantityReceived);
}

/**
 * Whether one receipt entry is acceptable: a whole number from zero — a
 * shortfall is the entire reason this screen exists — up to what remains.
 * Anything above `remaining` is not a partial receipt, it is a claim that
 * more arrived than was ever dispatched.
 */
export function isReceiveEntryValid(value: string, remaining: number): boolean {
  if (!/^\d+$/.test(value)) return false;
  const parsed = Number(value);
  return parsed >= 0 && parsed <= remaining;
}

/**
 * Whether a quantity short of what remains needs a reason before it can be
 * submitted (FR-WHS-8) — receiving everything still outstanding is not a
 * discrepancy, receiving less than that is, and has to say why.
 */
export function requiresDiscrepancyReason(
  quantity: number,
  remaining: number,
): boolean {
  return quantity > 0 && quantity < remaining;
}

/**
 * The payload for `receiveTransfer`: one entry per line that actually
 * received something this round.
 *
 * A line left at zero is omitted rather than sent as `{ quantity: 0 }` —
 * the adapter treats "not present" and "present with zero" identically
 * (see `receiveTransfer` in the fixture adapter), so there is nothing to
 * gain from sending the zero and a smaller payload is easier to reason
 * about at the call site. `reasons` is keyed the same way `drafts` is; a
 * reason is only ever attached to an entry that needs one.
 */
export function buildReceiveInput(
  lines: readonly TransferLine[],
  drafts: Readonly<Record<string, string>>,
  reasons: Readonly<Record<string, string>> = {},
): { lineId: Id; quantity: number; discrepancyReason?: string }[] {
  const entries: {
    lineId: Id;
    quantity: number;
    discrepancyReason?: string;
  }[] = [];
  for (const line of lines) {
    const value = drafts[line.id];
    if (value === undefined) continue;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) continue;

    const entry: { lineId: Id; quantity: number; discrepancyReason?: string } =
      { lineId: line.id, quantity: parsed };
    if (requiresDiscrepancyReason(parsed, remainingToReceive(line))) {
      const reason = reasons[line.id]?.trim();
      if (reason) entry.discrepancyReason = reason;
    }
    entries.push(entry);
  }
  return entries;
}

/** Whether any line in the draft has something to record this round. */
export function hasReceiveEntries(
  lines: readonly TransferLine[],
  drafts: Readonly<Record<string, string>>,
): boolean {
  return buildReceiveInput(lines, drafts).length > 0;
}

/**
 * Whether the whole receive draft is ready to submit: every entered quantity
 * is a valid whole number up to what remains, and every entry short of what
 * remains has a reason. The gate `TransferDetail` disables "Record receipt"
 * on.
 */
export function isReceiveDraftValid(
  lines: readonly TransferLine[],
  drafts: Readonly<Record<string, string>>,
  reasons: Readonly<Record<string, string>>,
): boolean {
  for (const line of lines) {
    const remaining = remainingToReceive(line);
    const value = drafts[line.id] ?? "0";
    if (!isReceiveEntryValid(value, remaining)) return false;

    const parsed = Number(value);
    if (
      requiresDiscrepancyReason(parsed, remaining) &&
      !reasons[line.id]?.trim()
    ) {
      return false;
    }
  }
  return hasReceiveEntries(lines, drafts);
}
